import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchScholarshipsByStatus } from "../store/slices/scholarshipSlice";
import { useSnackbar } from "notistack";
import { Award, AlertCircle } from "lucide-react";
import { useSidebarContext } from "../scenes/global/sidebar/SidebarContext";

const ApprovedScholarships = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { theme, gradient } = useSidebarContext();
  const { scholarships, loading, error } = useSelector(
    (state) => state.scholarship
  );

  useEffect(() => {
    const status = "Approved";
    dispatch(fetchScholarshipsByStatus(status))
      .unwrap()
      .then(() => {
        enqueueSnackbar("Scholarships loaded successfully!", {
          variant: "success",
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
          style: { backgroundColor: `${theme.mui.bgA}`, color: "white" },
        });
      })
      .catch((error) => {
        enqueueSnackbar(error.message || "Failed to load scholarships", {
          variant: "error",
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
          style: { backgroundColor: "#dc2626", color: "white" },
        });
      });
  }, [dispatch, enqueueSnackbar]);

  const formatPKR = (amount) => {
    return new Intl.NumberFormat("en-PK", {
      style: "currency",
      currency: "PKR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (loading)
    return (
      <div
        className={`flex justify-center items-center min-h-screen ${gradient} rounded-xl shadow-lg`}
      >
        <div className="flex flex-col items-center gap-4">
          <div
            className={`w-8 h-8 border-4 ${theme.tailwind.border25} border-t-transparent rounded-full animate-spin`}
          ></div>
          <p className={`${theme.tailwind.text9} animate-pulse`}>
            Loading Scholarships...
          </p>
        </div>
      </div>
    );

  if (error)
    return (
      <div
        className={`flex justify-center items-center min-h-screen ${gradient}`}
      >
        <div className="bg-red-500/10 px-6 py-4 rounded-lg border border-red-500/20">
          <p className="text-red-400">
            Error: {error?.message || "An error occurred"}
          </p>
        </div>
      </div>
    );

  return (
    <div className={`min-h-screen ${gradient}`}>
      <div className="container mx-auto p-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <div className={`p-2 ${theme.tailwind.bg58} rounded-lg`}>
            <Award className={`w-8 h-8 ${theme.tailwind.text9}`} />
          </div>
          <h1
            className={`text-4xl font-bold ${theme.tailwind.text9} tracking-tight`}
          >
            Approved Scholarships
          </h1>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div
            className={`${theme.tailwind.bg2} border ${theme.tailwind.border19} rounded-xl p-6 backdrop-blur`}
          >
            <h3 className={`${theme.tailwind.text9} text-sm font-medium mb-2`}>
              Total Scholarships
            </h3>
            <p className={`text-2xl font-bold ${theme.tailwind.texting}`}>
              {scholarships.length}
            </p>
          </div>

          <div
            className={`${theme.tailwind.bg2} border ${theme.tailwind.border19} rounded-xl p-6 backdrop-blur`}
          >
            <h3 className={`${theme.tailwind.text9} text-sm font-medium mb-2`}>
              Total Amount
            </h3>
            <p className={`text-2xl font-bold ${theme.tailwind.texting}`}>
              {formatPKR(
                scholarships.reduce((sum, s) => sum + s.amount, 0) || 0
              )}
            </p>
          </div>

          <div
            className={`${theme.tailwind.bg2} border ${theme.tailwind.border19} rounded-xl p-6 backdrop-blur`}
          >
            <h3 className={`${theme.tailwind.text9} text-sm font-medium mb-2`}>
              Average Amount
            </h3>
            <p className={`text-2xl font-bold ${theme.tailwind.texting}`}>
              {scholarships.length > 0
                ? formatPKR(
                    scholarships.reduce((sum, s) => sum + s.amount, 0) /
                      scholarships.length
                  )
                : formatPKR(0)}
            </p>
          </div>
        </div>

        {/* Table with Empty State */}
        <div
          className={`relative overflow-hidden rounded-xl border ${theme.tailwind.border19} ${theme.tailwind.bg2} backdrop-blur shadow-2xl`}
        >
          <div
            className={`absolute inset-0 ${theme.tailwind.bgGradient22}`}
          ></div>
          <div className="relative overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className={`border-b ${theme.tailwind.border19}`}>
                  <th
                    className={`px-6 py-5 text-left font-medium ${theme.tailwind.text9}`}
                  >
                    Name
                  </th>
                  <th
                    className={`px-6 py-5 text-left font-medium ${theme.tailwind.text9}`}
                  >
                    Email
                  </th>
                  <th
                    className={`px-6 py-5 text-left font-medium ${theme.tailwind.text9}`}
                  >
                    Amount
                  </th>
                  <th
                    className={`px-6 py-5 text-left font-medium ${theme.tailwind.text9}`}
                  >
                    Status
                  </th>
                  <th
                    className={`px-6 py-5 text-left font-medium ${theme.tailwind.text9}`}
                  >
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${theme.tailwind.divide1}`}>
                {scholarships.length > 0 ? (
                  scholarships.map((scholarship) => (
                    <tr
                      key={scholarship._id}
                      className={`group transition-colors ${theme.tailwind.bgHover15}`}
                    >
                      <td
                        className={`px-6 py-4 ${theme.tailwind.texting1} whitespace-nowrap font-medium`}
                      >
                        {scholarship.name}
                      </td>
                      <td
                        className={`px-6 py-4 ${theme.tailwind.texting1} whitespace-nowrap`}
                      >
                        {scholarship.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`font-medium ${theme.tailwind.text9}`}>
                          {formatPKR(scholarship.amount)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${theme.tailwind.bg58} ${theme.tailwind.text9} border ${theme.tailwind.border24}`}
                        >
                          {scholarship.status}
                        </span>
                      </td>
                      <td
                        className={`px-6 py-4 ${theme.tailwind.texting1} whitespace-nowrap`}
                      >
                        {new Date(scholarship.date).toLocaleDateString(
                          "en-GB",
                          {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          }
                        )}
                        $
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-16">
                      <div className="flex flex-col items-center justify-center">
                        <div
                          className={`p-3 ${theme.tailwind.bg17} rounded-full mb-4`}
                        >
                          <AlertCircle
                            className={`w-8 h-8 ${theme.tailwind.text9}`}
                          />
                        </div>
                        <h3
                          className={`text-xl font-medium ${theme.tailwind.text9} mb-2`}
                        >
                          No Scholarships Found
                        </h3>
                        <p
                          className={`${theme.tailwind.texting2} text-center max-w-md`}
                        >
                          There are currently no approved scholarships in the
                          system. Approved scholarships will appear here when
                          available.
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApprovedScholarships;
